import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { LayoutUsage } from '@barthauer/abc/arena';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import { ProfileInfo, UserService } from '@barthauer/abc/core/authentication';
import { CacheStorageAdapter } from '@barthauer/abc/core/data';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import { StateStorage } from '@barthauer/abc/ui-components';
import { BasysArenaComponent } from '@barthauer/basys/arena';
import { Status } from 'libs/abc/arena/src/lib/abc-arena.service';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { forkJoin, of, Subscription } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { BasysMapFrameService } from '../../basys-map-frame.service';
import { NamedObjectInfo } from '../../models/mapsData.model';

@Component({
  selector: 'app-object-info',
  templateUrl: './object-info.component.html',
  styleUrls: ['./object-info.component.css'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class ObjectInfoComponent implements OnDestroy, OnInit {
  public activeObjectInfo: NamedObjectInfo;
  public noLayoutPath: boolean;
  private subscriptions: Subscription[] = [];
  private arenaComponent: BasysArenaComponent;
  @ViewChild('dynamic', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  public res = ResourceSet.Empty;
  private lastObjectTypeNameForStorageKey: string;

  constructor(
    public frame: BasysMapFrameService,
    private resourceService: ResourceManagerService,
    private abcCore: AbcCore,
    private userService: UserService,
  ) {
    this.subscriptions.push(
      this.frame.lib
        .getObjectInfoData()
        .pipe(
          concatMap((objectInfos) => {
            if (objectInfos && objectInfos.length > 0) {
              const objectInfo: DataEntity = objectInfos[0];
              return this.frame.objectSpace.getObjectType(objectInfo.objectType).pipe(
                map((otvm) => {
                  const namedObjectInfo: NamedObjectInfo = new NamedObjectInfo();
                  namedObjectInfo.id = objectInfo.id;
                  namedObjectInfo.objectTypeName = otvm.name;

                  if (this.lastObjectTypeNameForStorageKey !== otvm.name) {
                    const StorageKeyInfo = forkJoin(
                      this.userService.getCurrentLoginInfo(),
                      this.frame.getCurrentProfile(),
                    );

                    StorageKeyInfo.subscribe((data) => {
                      const objectInfoStorageKey =
                        ConvertHelper.stringReplaceSpecialCharacters(data[0].displayName, ' ', '_', true) +
                        '_' +
                        ConvertHelper.stringReplaceSpecialCharacters(data[1].id, ' ', '_', true) +
                        StateStorage.objectInfo +
                        ConvertHelper.stringReplaceSpecialCharacters(otvm.displayTextPlural, ' ', '_', true);

                      this.frame.lib.mapInfoStateStorageKey.next(objectInfoStorageKey);
                      this.lastObjectTypeNameForStorageKey = otvm.name;
                    });
                  }
                  return namedObjectInfo;
                }),
              );
            } else {
              return of(null);
            }
          }),
        )
        .subscribe(
          async (noi) => {
            if (noi) {
              this.activeObjectInfo = noi as NamedObjectInfo;
              if (this.frame.frameConfig.arenaCurrentSettings) {
                // Arena initialisation for BaSYS maps/opera
                if (!this.arenaComponent) {
                  this.initArena((await this.frame.getCurrentProfile(true).toPromise())?.settings?.arena?.isReadOnly);
                }
                this.frame.frameConfig.arenaCurrentSettings.findArenaLayout(
                  this.arenaComponent.arenaName,
                  this.activeObjectInfo.objectTypeName,
                  LayoutUsage.Standard,
                );
              } else {
                this.noLayoutPath = true;
                this.frame.lib.arenaLayoutCaption.next('');
              }
            }
          },
          (error) => {
            this.abcCore.error.publish(error);
          },
        ),
    );
    this.subscriptions.push(
      this.abcCore.persist.afterPersistPendingChanges$.subscribe(
        (result) => {
          if (Array.isArray(result)) {
            const dataEntities = result.map((_) => new DataEntity(_.objectType, _.id));
            this.frame.map.refreshMapLayer(dataEntities);
          }
        },
        (err) => {
          this.abcCore.error.publish(err);
        },
      ),
    );
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('basys-map-frame');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
  }

  // Arena integration for BaSYS maps/opera
  private initArena(isReadOnly: boolean = null) {
    this.arenaComponent = <BasysArenaComponent>(
      this.frame.loadComponent(BasysArenaComponent, this.viewContainerRef).instance
    );
    this.arenaComponent.arenaName = ProfileInfo.defaultArenaName;
    this.arenaComponent.renderHigna = false;
    this.arenaComponent.renderHigon = false;
    this.arenaComponent.renderNavigation = false;
    this.arenaComponent.renderHeader = false;
    this.arenaComponent.renderDrawer = false;
    this.arenaComponent.isStandaloneDataForm = true;
    this.arenaComponent.crudPermissions = {
      DenyInsert: true,
      DenyDelete: true,
      DenyUpdate: CacheStorageAdapter.isOffline,
      IsReadOnly: CacheStorageAdapter.isOffline ? true : isReadOnly,
    };
    this.subscriptions.push(
      this.arenaComponent.arenaStatus().subscribe(
        (arena) => {
          let layoutCaption: string = null;
          if (arena.Status === Status.Success) {
            layoutCaption = arena.LayoutCaption;
            this.noLayoutPath = false;
          } else if (arena.Status === Status.Error) {
            this.noLayoutPath = true;
            this.frame.lib.arenaLayoutCaption.next('');
          }
          this.frame.lib.arenaLayoutCaption.next(layoutCaption);
        },
        (error) => {
          this.abcCore.error.publish(error);
        },
      ),
    );
    this.subscriptions.push(
      this.frame.frameConfig.arenaCurrentSettings?.onFindArenaLayout().subscribe(
        (arenaLayout) => {
          if (arenaLayout?.layout.fullName) {
            this.arenaComponent.defaultObjectType = this.activeObjectInfo.objectTypeName;
            this.arenaComponent.openLayoutByPath(arenaLayout.arenaName, arenaLayout.layout.fullName, [
              this.activeObjectInfo.id,
            ]);
            this.frame.isProfileChanged = false;
          } else {
            this.noLayoutPath = true;
            this.frame.lib.arenaLayoutCaption.next('');
          }
        },
        (error) => {
          this.noLayoutPath = true;
          this.frame.lib.arenaLayoutCaption.next('');
        },
      ),
    );
  }
}
